// Catalogue import API contracts (`/imports/*`, plan §12). Vendor-neutral: a source is a
// connector `kind` from the server's connector registry, shown by its `display_name`.
// Envelope fields are snake_case; the domain documents they carry (rules, decisions,
// outcomes, the verification report, the plan diff) keep their domain (camelCase) shape,
// like state-rule states in the catalogue API, so a client can post them back unchanged.
import { z } from 'zod';
import { type ProductRole, productRoleSchema } from '../catalogue/roles.js';
import { CHECK_STATUSES, type CheckStatus } from '../checks/index.js';
import { CONNECTOR_CAPABILITIES } from '../connections/index.js';
import {
  DECISION_KINDS,
  DIFFED_SECTIONS,
  type ImportPlan,
  OUTCOME_KINDS,
  type PlanDiff,
  REVIEW_KINDS,
  type RecordOutcome,
  type VerificationReport,
  decisionSchema,
  importRulesSchema,
  manualDecisionSchema,
} from '../migration/index.js';

const instant = z.iso.datetime({ offset: true });
const count = z.int().min(0);
const connectorKind = z.string().regex(/^[a-z][a-z0-9_]*$/, 'Expected a connector kind');

/** Largest page the review-item and outcome lists return. */
export const IMPORT_PAGE_MAX = 200;
/** Page size when `limit` is not given. */
export const IMPORT_PAGE_DEFAULT = 50;
const limit = z.coerce.number().int().min(1).max(IMPORT_PAGE_MAX).default(IMPORT_PAGE_DEFAULT);
const cursor = z.string().min(1).max(1024).optional();

// ---------------------------------------------------------------------------------------
// Run states

/**
 * Pipeline states (plan §12.1): `extracting` (reading the source) → `planning` (rules →
 * plan → verification report) → `awaiting_review` (review items open) or `ready` →
 * `loading` → `loaded`. `failed` and `cancelled` are final too.
 */
export const IMPORT_RUN_STATUSES = [
  'extracting',
  'planning',
  'awaiting_review',
  'ready',
  'loading',
  'loaded',
  'failed',
  'cancelled',
] as const;
/** One of {@link IMPORT_RUN_STATUSES}. */
export type ImportRunStatus = (typeof IMPORT_RUN_STATUSES)[number];
/** zod schema for a run status. */
export const importRunStatusSchema = z.enum(IMPORT_RUN_STATUSES);
/** States in which a job is working on the run (one such run per connection). */
export const IMPORT_RUN_BUSY_STATUSES = ['extracting', 'planning', 'loading'] as const;
/**
 * States a run can always be cancelled from. A `loading` run can be cancelled too, but only
 * when no job holds it (its worker died): the API answers 409 `run_busy` otherwise.
 */
export const IMPORT_RUN_CANCELLABLE_STATUSES = [
  'extracting',
  'planning',
  'awaiting_review',
  'ready',
] as const;
/** Final states. */
export const IMPORT_RUN_FINAL_STATUSES = ['loaded', 'failed', 'cancelled'] as const;
/** States a run can be re-planned from (after a rules change). */
export const IMPORT_RUN_REPLANNABLE_STATUSES = ['awaiting_review', 'ready'] as const;

/** Why an extract is waiting before its next request (see `progress.waiting`). */
export const IMPORT_WAIT_REASONS = ['rate_limited', 'source_unavailable'] as const;

// ---------------------------------------------------------------------------------------
// Connectors and connections

/** `GET /imports/connectors`: sources a catalogue can be imported from. */
export const importConnectorSchema = z.object({
  kind: connectorKind,
  /** Shown to users; never hard-coded in a client. */
  display_name: z.string().min(1),
  capabilities: z.array(z.enum(CONNECTOR_CAPABILITIES)),
  /** The source's production API address. */
  default_base_url: z.url(),
  /** Pattern an `env:` credential variable must match, e.g. `^X_[A-Z0-9_]+_TOKEN$`. */
  credential_env_pattern: z.string(),
  /** The built-in development fake can be used (local and test environments only). */
  dev_source: z.object({ base_url: z.url(), credential_ref: z.string() }).nullable(),
  /** The most a connection may ask of the source (shared by every run of a connection). */
  rate_ceiling: z.object({
    max_requests: z.int().min(1),
    window_seconds: z.int().min(1),
  }),
  /**
   * Credential refs the caller's account can use for a new connection here: `builtin:dev`
   * where the fake exists, plus the `env:` refs the operator granted **this account**
   * (D-031) that the server has a value for. Names only, never values, and never another
   * account's grants. The Admin offers these as a list; it never asks for a token.
   */
  credential_refs: z.array(z.string()),
});
/** One connector. */
export type ImportConnector = z.infer<typeof importConnectorSchema>;
/** `GET /imports/connectors` response. */
export const importConnectorListResponseSchema = z.object({
  connectors: z.array(importConnectorSchema),
});

/**
 * Where the server finds the source credential: `env:<VARIABLE>` (a server environment
 * variable or secret) or `builtin:dev` (the development fake). Never the credential.
 * An `env:` ref must be granted to the account by the operator (D-031): otherwise the
 * API answers 403 `credential_not_granted`.
 */
export const credentialRefSchema = z
  .string()
  .regex(
    /^(env:[A-Z][A-Z0-9_]*|builtin:[a-z][a-z0-9_]*)$/,
    'Expected env:<VARIABLE> or builtin:dev',
  );

/**
 * Requests per window the import may send to the source. The server clamps it to the
 * connector's ceiling (`rate_ceiling`): a tenant can only lower it (D-031).
 */
export const rateBudgetSchema = z.object({
  max_requests: z.int().min(1).max(10_000),
  window_seconds: z.int().min(1).max(86_400),
});
/** A rate budget. */
export type RateBudgetDto = z.infer<typeof rateBudgetSchema>;

/** `POST /imports/connections` body. */
export const importConnectionCreateRequestSchema = z.object({
  connector_kind: connectorKind,
  credential_ref: credentialRefSchema,
  /** Default: the connector's production address (or the dev fake's for `builtin:dev`). */
  base_url: z.url().optional(),
  /** Default: `test` for the dev fake, else `live`. */
  environment: z.enum(['test', 'live']).optional(),
  /** Default (and most): the connector's `rate_ceiling`, e.g. 60 requests per 300 s. */
  rate_budget: rateBudgetSchema.optional(),
});
/** `POST /imports/connections` body. */
export type ImportConnectionCreateRequest = z.infer<typeof importConnectionCreateRequestSchema>;

/** Integration modes (plan §11); V1 imports use `off` and `read_only` only. */
export const INTEGRATION_MODES = ['off', 'read_only', 'dry_run', 'live'] as const;

/** A source connection. */
export const importConnectionSchema = z.object({
  id: z.uuid(),
  connector_kind: connectorKind,
  display_name: z.string(),
  base_url: z.string(),
  credential_ref: credentialRefSchema,
  environment: z.enum(['test', 'live']),
  integration_id: z.uuid(),
  mode: z.enum(INTEGRATION_MODES),
  /** The source account the owner confirmed; every run checks it first. */
  expected_target_identity: z.string().nullable(),
  /** What the source last said it is (to show before confirming). */
  reported_target: z
    .object({ identity: z.string(), name: z.string().nullable(), at: instant })
    .nullable(),
  target_confirmed_at: instant.nullable(),
  rate_budget: rateBudgetSchema,
  last_error: z.string().nullable(),
  created_at: instant,
});
/** A source connection. */
export type ImportConnection = z.infer<typeof importConnectionSchema>;
/** `GET /imports/connections` response. */
export const importConnectionListResponseSchema = z.object({
  connections: z.array(importConnectionSchema),
});

/**
 * `POST /imports/connections/:id/confirm-target` body (owner only): the identity the owner
 * saw. It must equal what the source reports now.
 */
export const importConfirmTargetRequestSchema = z.object({ target_identity: z.string().min(1) });

/** `:id` path parameter. */
export const importIdParamsSchema = z.object({ id: z.uuid() });

/**
 * `POST /imports/connections/:id/identify`: asks the source again who it is (e.g. after the
 * first attempt failed) and answers with the connection, `reported_target` refreshed.
 * It spends the connection's shared request budget, so it needs `catalogue.edit` on all
 * branches (review CR1-24), like creating the connection.
 */
export const importIdentifyResponseSchema = importConnectionSchema;

// ---------------------------------------------------------------------------------------
// Domain documents on the wire

const recordTotalsSchema = z.object({
  in: count,
  out: count,
  byOutcome: z.record(z.enum(OUTCOME_KINDS), count),
});

/** The verification report (`@enp/domain` `VerificationReport`). */
export const verificationReportSchema = z.object({
  rulesRevision: count,
  status: z.enum(CHECK_STATUSES),
  items: recordTotalsSchema,
  variants: recordTotalsSchema,
  couldNotClassify: z.object({
    items: count,
    variants: count,
    refs: z.array(z.string()),
    summary: z.string(),
  }),
  checks: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      expected: count,
      checked: count,
      failures: count,
      status: z.enum(CHECK_STATUSES),
      summary: z.string(),
      failureRefs: z.array(z.string()),
    }),
  ),
  costCoverage: z.object({
    computable: count,
    total: count,
    notComputable: z.array(z.object({ productKey: z.string(), reason: z.string() })),
  }),
  reviewItemsByKind: z.record(z.enum(REVIEW_KINDS), count),
  unusedRules: count,
});

const sectionDiffSchema = z.object({
  added: z.array(z.string()),
  changed: z.array(z.string()),
  removed: z.array(z.string()),
});

/** A plan diff (`@enp/domain` `PlanDiff`). */
export const planDiffSchema = z.object({
  sections: z.record(z.enum(DIFFED_SECTIONS), sectionDiffSchema),
  unchanged: z.boolean(),
  summary: z.array(z.string()),
});

/** A record outcome (`@enp/domain` `RecordOutcome`). */
export const recordOutcomeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('mapped'), targetKey: z.string() }),
  z.object({
    kind: z.literal('merged_into_family'),
    familyKey: z.string(),
    productKey: z.string(),
  }),
  z.object({ kind: z.literal('state_mapping'), productKey: z.string(), state: z.string() }),
  z.object({ kind: z.literal('ignored'), reason: z.string() }),
  z.object({
    kind: z.literal('could_not_classify'),
    reason: z.enum([
      'category_role_unknown',
      'twin_ambiguous',
      'twin_unpaired',
      'partner_not_classified',
      'state_group_uncertain',
    ]),
  }),
]);

// Compile-time proof that the wire schemas and the domain types agree both ways.
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
const _reportMatches: Same<z.infer<typeof verificationReportSchema>, VerificationReport> = true;
const _diffMatches: Same<z.infer<typeof planDiffSchema>, PlanDiff> = true;
const _outcomeMatches: Same<z.infer<typeof recordOutcomeSchema>, RecordOutcome> = true;
void _reportMatches;
void _diffMatches;
void _outcomeMatches;

// ---------------------------------------------------------------------------------------
// Runs

/** Counts of a plan, by record kind. */
export const importPlanSummarySchema = z.object({
  categories: count,
  families: count,
  products: count,
  identifiers: count,
  recipes: count,
  /** Recipes held back until a review item is decided. */
  recipes_blocked: count,
  recipe_lines: count,
  state_rules: count,
  channel_mappings: count,
  uom_conversions: count,
  review_items: count,
  unused_rules: count,
});
/** Counts of a plan. */
export type ImportPlanSummary = z.infer<typeof importPlanSummarySchema>;

/** Summarises a plan (pure). */
export function summarisePlan(plan: ImportPlan): ImportPlanSummary {
  return {
    categories: plan.categories.length,
    families: plan.families.length,
    products: plan.products.length,
    identifiers: plan.identifiers.length,
    recipes: plan.recipes.length,
    recipes_blocked: plan.recipes.filter((r) => r.blocked).length,
    recipe_lines: plan.recipes.reduce((n, r) => n + r.version.lines.length, 0),
    state_rules: plan.stateRules.length,
    channel_mappings: plan.channelMappings.length,
    uom_conversions: plan.uomConversions.length,
    review_items: plan.reviewItems.length,
    unused_rules: plan.unusedRules.length,
  };
}

/**
 * The roles of the product each source record became, by source ref (pure). A record that
 * became part of several products (a family's sizes) gets the roles of all of them.
 */
export function rolesBySourceRef(plan: ImportPlan): Map<string, ProductRole[]> {
  const roles = new Map<string, ProductRole[]>();
  for (const p of plan.products) {
    for (const ref of p.sourceRefs) {
      const had = roles.get(ref) ?? [];
      roles.set(ref, [...had, ...p.roles.filter((r) => !had.includes(r))]);
    }
  }
  return roles;
}

const perTable = z.record(z.string(), count);

/** What a run read and what its load did. */
export const importRunCountsSchema = z.object({
  extract: z
    .object({
      requests: count,
      pages: count,
      items: count,
      variants: count,
      categories: count,
      suppliers: count,
      stores: count,
      inventory_levels: count,
      /**
       * What the adapter noticed while translating the source (never silently dropped):
       * variants the source deleted on live items (planned as ignored), records seen twice
       * (kept once) and references to records the extract does not have.
       */
      notes: z
        .object({ deleted_variants: count, duplicates_dropped: count, unknown_refs: count })
        .optional(),
    })
    .optional(),
  load: z
    .object({
      inserted: perTable,
      updated: perTable,
      archived: perTable,
      unchanged: perTable,
      /**
       * Rows whose change a person owns (D-032): left alone, with a catalogue review item
       * each (`GET /catalogue/review-items`). Absent on loads before M5.
       */
      kept: perTable.optional(),
    })
    .optional(),
});
/** Run counts. */
export type ImportRunCounts = z.infer<typeof importRunCountsSchema>;

/** A run in a list. */
export const importRunSummarySchema = z.object({
  id: z.uuid(),
  connection_id: z.uuid(),
  connector_kind: connectorKind,
  status: importRunStatusSchema,
  rules_revision: count.nullable(),
  /** The verification report's overall status, once planned. */
  report_status: z.enum(CHECK_STATUSES).nullable(),
  review_items: count.nullable(),
  counts: importRunCountsSchema,
  started_by_admin_user_id: z.uuid(),
  started_at: instant,
  finished_at: instant.nullable(),
  error_code: z.string().nullable(),
  error: z.string().nullable(),
});
/** A run in a list. */
export type ImportRunSummary = z.infer<typeof importRunSummarySchema>;

/** `GET /imports/runs/:id`. */
export const importRunDetailSchema = importRunSummarySchema.extend({
  /** Where the current step is, e.g. `{ stage: 'items', done: 750 }`. */
  progress: z.object({
    stage: z.string().optional(),
    done: count.optional(),
    total: count.optional(),
    /**
     * The source asked us to slow down (or failed briefly) and the next request waits until
     * `until`; the run carries on by itself.
     */
    waiting: z.object({ reason: z.enum(IMPORT_WAIT_REASONS), until: instant }).optional(),
  }),
  plan_summary: importPlanSummarySchema.nullable(),
  report: verificationReportSchema.nullable(),
  /** Changes against the last loaded run of the same source (`null`: nothing loaded yet). */
  diff: planDiffSchema.nullable(),
  diff_base_run_id: z.uuid().nullable(),
  load_requested_at: instant.nullable(),
  load_requested_by_admin_user_id: z.uuid().nullable(),
});
/** A run with its report. */
export type ImportRunDetail = z.infer<typeof importRunDetailSchema>;

/** `POST /imports/runs` body. */
export const importRunCreateRequestSchema = z.object({ connection_id: z.uuid() });

/** `GET /imports/runs` query. */
export const importRunListQuerySchema = z.object({
  connection_id: z.uuid().optional(),
  status: importRunStatusSchema.optional(),
  limit,
  cursor,
});
/** `GET /imports/runs` response. */
export const importRunListResponseSchema = z.object({
  items: z.array(importRunSummarySchema),
  next_cursor: z.string().nullable(),
});

/** `GET /imports/runs/:id/review-items` query. */
export const importReviewItemListQuerySchema = z.object({
  kind: z.enum(REVIEW_KINDS).optional(),
  limit,
  cursor,
});

/** A source record named by a review item, so people see names rather than refs. */
export const importSourceRecordSchema = z.object({
  source_ref: z.string(),
  record_kind: z.enum(['item', 'variant', 'category']),
  /** Source name (variant: item name plus options); `null` when the source has no such record. */
  name: z.string().nullable(),
  sku: z.string().nullable(),
  /**
   * The source category of an item or variant (its item's), so a person can see what differs
   * between records; `null` when it has none. Absent on rows written before it existed.
   */
  category: z.string().nullable().optional(),
});
/** A source record of a review item. */
export type ImportSourceRecord = z.infer<typeof importSourceRecordSchema>;

/**
 * A review item. `suggested_decisions` can be posted back as they are: together they are
 * one suggestion (applied together), not alternatives.
 */
export const importReviewItemSchema = z.object({
  key: z.string(),
  kind: z.enum(REVIEW_KINDS),
  source_refs: z.array(z.string()),
  /** The records of `source_refs`, in the same order. */
  source_records: z.array(importSourceRecordSchema),
  message: z.string(),
  suggested_decisions: z.array(manualDecisionSchema),
});
/** A review item. */
export type ImportReviewItem = z.infer<typeof importReviewItemSchema>;

/** `GET /imports/runs/:id/review-items` response. */
export const importReviewItemListResponseSchema = z.object({
  items: z.array(importReviewItemSchema),
  /** Matching items in the whole run (not just this page). */
  total: count,
  next_cursor: z.string().nullable(),
});

/** `GET /imports/runs/:id/outcomes` query: the per-source-record drill-down. */
export const importOutcomeListQuerySchema = z.object({
  outcome: z.enum(OUTCOME_KINDS).optional(),
  record_kind: z.enum(['item', 'variant']).optional(),
  /** Only these records: comma-separated source refs (`item:…`, `variant:…`), at most 200. */
  source_refs: z
    .string()
    .max(20_000)
    .transform((s) =>
      s
        .split(',')
        .map((r) => r.trim())
        .filter((r) => r !== ''),
    )
    .pipe(z.array(z.string()).max(IMPORT_PAGE_MAX))
    .optional(),
  /** Only records whose name or SKU contains this text (case ignored). */
  q: z.string().trim().min(1).max(100).optional(),
  limit,
  cursor,
});

/** What happened to one source record. */
export const importOutcomeSchema = z.object({
  source_ref: z.string(),
  record_kind: z.enum(['item', 'variant']),
  item_ref: z.string(),
  /** Source name (item name; variant: item name plus options). */
  name: z.string().nullable(),
  sku: z.string().nullable(),
  outcome: recordOutcomeSchema,
  /**
   * The roles of the product the record became (`null`: it became none). Lets a picker
   * offer only sensible targets. Absent on rows written before it existed.
   */
  roles: z.array(productRoleSchema).nullable().optional(),
});
/** What happened to one source record. */
export type ImportOutcome = z.infer<typeof importOutcomeSchema>;
/** `GET /imports/runs/:id/outcomes` response. */
export const importOutcomeListResponseSchema = z.object({
  items: z.array(importOutcomeSchema),
  total: count,
  next_cursor: z.string().nullable(),
});

/** One decision to store. */
const decisionEntrySchema = z.object({
  /** The review item this answers (kept with the decision for the audit trail). */
  review_item_key: z.string().min(1).optional(),
  source_ref: manualDecisionSchema.shape.sourceRef,
  decision: decisionSchema,
});
/**
 * Takes back the stored decision about `source_ref` in the slot of `revoke` (a decision
 * kind; kinds that share a slot, like `pair_twin`/`not_twin`, revoke each other). Answered
 * 409 `decision_not_found` when that slot holds no decision.
 */
const revocationEntrySchema = z.object({
  review_item_key: z.string().min(1).optional(),
  source_ref: manualDecisionSchema.shape.sourceRef,
  revoke: z.enum(DECISION_KINDS),
});

/**
 * `POST /imports/runs/:id/decisions` body: decisions (and revocations) are stored as the
 * next rules revision, in order, then the run is re-planned. Decisions are owned by this
 * endpoint: `PUT /imports/rules` never changes them.
 */
export const importDecisionsRequestSchema = z.object({
  decisions: z
    .array(z.union([decisionEntrySchema, revocationEntrySchema]))
    .min(1)
    .max(5_000),
});
/** `POST /imports/runs/:id/decisions` body. */
export type ImportDecisionsRequest = z.infer<typeof importDecisionsRequestSchema>;

/** A source category of a run's catalogue (for the category → roles rules). */
export const importSourceCategorySchema = z.object({
  source_ref: z.string(),
  name: z.string(),
  /** The source has deleted it (it is planned archived). */
  deleted: z.boolean(),
  /** Source items in it. */
  items: count,
});
/** A source category. */
export type ImportSourceCategory = z.infer<typeof importSourceCategorySchema>;
/** `GET /imports/runs/:id/categories` response, sorted by name. */
export const importSourceCategoryListResponseSchema = z.object({
  items: z.array(importSourceCategorySchema),
  /** Items with no category. */
  uncategorised_items: count,
});

// ---------------------------------------------------------------------------------------
// Rules

/** `GET /imports/rules` response (and `PUT` response). */
export const importRulesResponseSchema = z.object({
  /** 0 with the neutral defaults when the account has stored none. */
  revision: count,
  rules: importRulesSchema,
  updated_at: instant.nullable(),
});
/** `GET /imports/rules` response. */
export type ImportRulesResponse = z.infer<typeof importRulesResponseSchema>;

/**
 * `PUT /imports/rules` body: a whole rules document **without its decisions**. `base_revision`
 * must be the current revision (someone else's change is never overwritten); the server
 * stores it as the next, carrying the current decisions forward unchanged. `revision` and
 * `decisions`, if sent (e.g. a GET response posted back), are ignored: decisions change
 * only through `POST /imports/runs/:id/decisions`.
 */
export const importRulesUpdateRequestSchema = z.object({
  base_revision: count,
  // Unknown keys are stripped, so a posted-back GET document is accepted as it is.
  rules: importRulesSchema.omit({ revision: true, decisions: true }),
});
/** `PUT /imports/rules` body. */
export type ImportRulesUpdateRequest = z.infer<typeof importRulesUpdateRequestSchema>;

/**
 * Who may load a run, by its verification report status (D-037):
 * - `pass` → `open`: anyone with `catalogue.edit` on all branches;
 * - `partial` / `could_not_run` → `owner_acknowledges`: only the owner
 *   (`integration.go_live`, all branches), who sends back the exact status they saw;
 * - `fail` (a broken invariant, e.g. records in ≠ records out) or no report →
 *   `not_loadable`: nobody; fix the cause and re-check.
 */
export type ImportLoadRule = 'open' | 'owner_acknowledges' | 'not_loadable';

/** The {@link ImportLoadRule} for a report status (`null`: no report yet). */
export function importLoadRuleOf(status: CheckStatus | null | undefined): ImportLoadRule {
  if (status === 'pass') return 'open';
  if (status === 'partial' || status === 'could_not_run') return 'owner_acknowledges';
  return 'not_loadable';
}

/**
 * `POST /imports/runs/:id/load` body. The gate is checked on the run as it is inside the
 * load transaction (the row is locked), in this order:
 * - 409 `review_items_open` / `run_not_ready`: the run is not `ready`;
 * - 409 `rules_outdated`: planned with an older rules revision (re-check it with
 *   `POST /imports/runs/:id/replan`);
 * - 409 `report_failed`: the report is `fail` and can never be loaded (D-037);
 * - 403 `owner_required`: the report is `partial` / `could_not_run` and the caller is not
 *   the owner;
 * - 409 `report_not_acknowledged`: the report is not `pass` and `acknowledge_status` does
 *   not name its current status.
 */
export const importLoadRequestSchema = z
  .object({ acknowledge_status: z.enum(CHECK_STATUSES).optional() })
  .nullish();
/** `POST /imports/runs/:id/load` body. */
export type ImportLoadRequest = z.infer<typeof importLoadRequestSchema>;
